import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import AuthShell from "@/components/AuthShell";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/app";
  const googleError = params.get("google_error");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isAuthenticated) navigate(next, { replace: true });
  }, [isAuthenticated, navigate, next]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email.trim().toLowerCase(), password);
      navigate(next, { replace: true });
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || "Login failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Welcome back." subtitle="Sign in">
      {googleError && (
        <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 text-sm text-red-700">
          Google sign-in didn&rsquo;t complete. Try again or use email + password.
        </div>
      )}
      <a
        href={`${API}/auth/google/start?next=${encodeURIComponent(next)}`}
        data-testid="login-google"
        className="w-full inline-flex items-center justify-center gap-3 px-4 py-3 mb-3 border-2 border-black bg-white hover:bg-[#FFFBE6] text-sm font-semibold"
      >
        <GoogleIcon /> Continue with Google
      </a>
      <a
        href={`${API}/auth/apple/start?next=${encodeURIComponent(next)}`}
        data-testid="login-apple"
        className="w-full inline-flex items-center justify-center gap-3 px-4 py-3 mb-5 border-2 border-black bg-black text-white hover:bg-neutral-800 text-sm font-semibold"
      >
        <AppleIcon /> Continue with Apple
      </a>
      <div className="flex items-center gap-3 mb-5 text-[10px] uppercase tracking-[0.18em] text-neutral-400">
        <div className="flex-1 h-px bg-neutral-300" /> or <div className="flex-1 h-px bg-neutral-300" />
      </div>
      <form onSubmit={handleSubmit} className="space-y-3" data-testid="login-form">
        <Field label="Email">
          <input
            type="email" required value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email"
            autoComplete="email"
            className="w-full px-4 py-2.5 border border-black outline-none text-[15px] bg-white focus:bg-[#FFFBE6]"
          />
        </Field>
        <Field
          label="Password"
          aside={
            <Link to="/forgot-password" className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 hover:text-black">
              Forgot?
            </Link>
          }
        >
          <input
            type="password" required value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="login-password"
            autoComplete="current-password"
            className="w-full px-4 py-2.5 border border-black outline-none text-[15px] bg-white focus:bg-[#FFFBE6]"
          />
        </Field>
        {error && <div data-testid="login-error" className="text-sm text-red-600">{error}</div>}
        <button
          type="submit" disabled={busy}
          data-testid="login-submit"
          className="w-full inline-flex items-center justify-center gap-2 mt-2 px-5 py-3 bg-black text-[#FFD600] hover:bg-[#FFD600] hover:text-black border-2 border-black text-sm tracking-[0.18em] uppercase font-bold disabled:opacity-50"
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          Sign in
        </button>
      </form>
      <div className="mt-6 text-sm text-center text-neutral-700">
        New here? <Link to="/?stay=1" className="font-semibold underline" data-testid="login-to-signup">Create an account</Link>.
      </div>
    </AuthShell>
  );
}

function Field({ label, aside, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="ticker-label !text-[10px]">{label}</label>
        {aside}
      </div>
      {children}
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.79 2.71v2.26h2.91c1.7-1.57 2.68-3.88 2.68-6.61z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.46-.8 5.95-2.18l-2.91-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.9v2.32A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.97 10.71A5.41 5.41 0 0 1 3.68 9c0-.59.1-1.16.29-1.71V4.97H.9A9 9 0 0 0 0 9c0 1.45.35 2.83.9 4.03l3.07-2.32z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 9 0 9 9 0 0 0 .9 4.97l3.07 2.32C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 384 512" fill="currentColor">
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zM256.6 105.4c30.3-35.9 27.6-68.6 26.7-80.4-26.8 1.5-57.8 18.2-75.5 38.7-19.5 22-31 49.2-28.5 79.8 29 2.2 55.5-12.7 77.3-38.1z" />
    </svg>
  );
}
